import {Component, EventEmitter, Inject} from '@angular/core';
import {ProjectId} from '../../types/ids';
import {Browser, browserFromBackendObject} from '../../types/browser';
import {isDefined} from '../../utils/utils';
import {map} from 'rxjs/operators';

import * as _ from 'lodash';
import {DeviceService} from '@webmate-web-core/shared/services/device/device.service';
import {AutomationPolicyConfig} from '@webmate-web-core/shared/types/browsersession';
import {
    BasicAuthInfo,
    BrowserSpecification,
    ClickDriverSpecification,
    DriverSpecification,
    DriverSpecificationType,
    ExpeditionComparisonTestConfig,
    LiveExpeditionSpec,
    SequentialDriverSpecification,
    URLListDriverSpecification
} from '@webmate-web-core/shared/types/testmgmt';
import {VehicleService} from '@webmate-web-core/shared/services/vehicle.service';
import {DevicePropertyNames} from '@webmate-web-core/shared/types/devices/device-properties';
import {BAConfig, ClickDriverConfigElement} from '@webmate-web-core/shared/types/test-config-helpers';
import {ModalToolContainerService, WebmateToolConfig} from '../modaltoolcontainer/modaltoolcontainer.service';
import {TOOL_CONTAINER_DATA} from '../modaltoolcontainer/modaltoolcontainer.component';
import {SelectChangeEvent} from 'primeng/select';

export class RegressionTestDialogToolConfig implements WebmateToolConfig {
    toolName = 'Configure Regression Test';
    scrollable = true;

    constructor(public projectId: ProjectId, public defaults: ExpeditionComparisonTestConfig, public createCallback: (conf: ExpeditionComparisonTestConfig) => void, public closeAfterCallback: boolean, public editMode: boolean = false, public existingNames: Array<string> = [], public btnLabel = 'EXECUTE TEST') {
        if (defaults.name) {
            this.toolName = `Configure ${defaults.name}`;
        }
    }
}

@Component({
    templateUrl: './regression-test-dialog.component.html',
    styleUrls: ['./regression-test-dialog.component.less'],
})
export class RegressionTestDialogComponent {

    projectId: ProjectId;

    constructor(@Inject(TOOL_CONTAINER_DATA) public data: RegressionTestDialogToolConfig, private deviceService: DeviceService,
                private toolService: ModalToolContainerService, private vehicleService: VehicleService) {
    }

    chooseDriverActive: boolean = true;

    name: string = '';
    description: string = '';

    urls: string[] = [''];
    crossUrls: string[] = [''];

    availableBrowsers: Browser[] = [];
    availableReferenceBrowsers: Browser[] = [];
    deployedBrowsers: Browser[] = [];

    simplifiedRef = true;
    chosenReferenceBrowser?: Browser = undefined;

    clearBrowser = new EventEmitter<void>();

    busy: boolean = true;

    basicAuthData: BAConfig[] = [];
    baUrlOptions: Array<{ value: string; label: string }> = [];

    clickDriverConfig: ClickDriverConfigElement[] = [];
    referenceAutomationPolicy?: AutomationPolicyConfig;


    /** The following map is used for issue tracking during CBT creation.
     * Each entry in the map relates to a certain issue (e.g. selected browser without proxy support)
     * and will be displayed in a confirmation dialog before starting the test.
     *
     * The map maps from a unique key for each issue type to an error message that will be shown.
     */
    testConfigurationIssues: Map<string, string> = new Map<string, string>();

    browserWithoutBasicAuthSelected: boolean = false;
    basicAuthErrorMessage?: string = undefined;

    advancedSettingsShown = false;

    isCreatingTest = false;

    ngOnInit() {
        this.name = this.data.defaults.name ? this.data.defaults.name : '';
        this.description = this.data.defaults.description ? this.data.defaults.description : '';

        // TODO validate driver and vehicle types
        if (this.data.defaults) {
            if (this.data.defaults.referenceExpeditionSpec && this.data.defaults.referenceExpeditionSpec.data.type === 'LiveExpeditionSpec') {
                const referenceSpec = this.data.defaults.referenceExpeditionSpec.data as LiveExpeditionSpec;
                this.referenceAutomationPolicy = referenceSpec.automationPolicy;
                if (referenceSpec.vehicleSpec) {
                    this.chosenReferenceBrowser = browserFromBackendObject(referenceSpec.vehicleSpec.browser);
                }
                if (referenceSpec.driverSpec) {
                    switch (referenceSpec.driverSpec.type) {
                        case 'URLListDriverSpecification':
                            this.urls = (referenceSpec.driverSpec as URLListDriverSpecification).urls;
                            if (this.urls.length === 0) {
                                this.urls = [''];
                            }
                            break;
                        case 'SequentialDriverSpecification': {
                            let rootDriverSpecification = referenceSpec.driverSpec as SequentialDriverSpecification;
                            let urlListDrivers = rootDriverSpecification.specs.filter(spec => spec.type == 'URLListDriverSpecification') as Array<URLListDriverSpecification>;
                            let clickDrivers = rootDriverSpecification.specs.filter(spec => spec.type == 'ClickDriverSpecification') as Array<ClickDriverSpecification>;
                            if (urlListDrivers.length != 1 || clickDrivers.length != 1) {
                                break;
                            }

                            this.urls = urlListDrivers[0].urls;
                            if (this.urls.length === 0) {
                                this.urls = [''];
                            }
                            this.mergeClickDriverConfig(clickDrivers[0].specs.map(spec => {
                                return {url: spec.url, xpaths: spec.xpaths} as ClickDriverConfigElement;
                            }));
                            break;
                        }
                        default:
                            break;
                    }
                }

                let ba = referenceSpec.basicAuth;
                if (ba && ba.length > 0) {
                    ba.forEach(e => {
                        let existing = this.basicAuthData.find(value => value.username === e.basic_auth_username && value.password === e.basic_auth_password);
                        if (!!existing) {
                            existing.urls.push(e.basic_auth_domain);
                        } else {
                            this.basicAuthData.push({
                                urls: [e.basic_auth_domain],
                                username: e.basic_auth_username,
                                password: e.basic_auth_password
                            });
                        }
                    });
                }

                this.updateBaUrlOptions();
                if (this.basicAuthData.length === 0) {
                    this.addBAConfig();
                }
            }
            if (this.data.defaults.comparisonExpeditionSpecs
                && this.data.defaults.comparisonExpeditionSpecs.type === 'List[ExpeditionSpec]'
                && this.data.defaults?.comparisonExpeditionSpecs?.data[0]
                && this.data.defaults?.comparisonExpeditionSpecs?.data[0].type === 'LiveExpeditionSpec') {
                const compSpec = this.data.defaults?.comparisonExpeditionSpecs?.data[0] as LiveExpeditionSpec;
                switch (compSpec.driverSpec.type) {
                    case 'URLListDriverSpecification': {
                        const compSpecDriver = compSpec.driverSpec as URLListDriverSpecification;
                        this.crossUrls = compSpecDriver.urls;
                        if (this.crossUrls.length === 0) {
                            this.crossUrls = [''];
                        }
                        break;
                    }
                    case 'SequentialDriverSpecification': {
                        let rootDriverSpecification = compSpec.driverSpec as SequentialDriverSpecification;

                        let urlListDrivers = rootDriverSpecification.specs.filter(spec => spec.type == 'URLListDriverSpecification') as Array<URLListDriverSpecification>;
                        let clickDrivers = rootDriverSpecification.specs.filter(spec => spec.type == 'ClickDriverSpecification') as Array<ClickDriverSpecification>;
                        if (urlListDrivers.length != 1 || clickDrivers.length != 1) {
                            break;
                        }

                        this.crossUrls = urlListDrivers[0].urls;
                        if (this.crossUrls.length === 0) {
                            this.crossUrls = [''];
                        }
                        this.mergeClickDriverConfig(clickDrivers[0].specs.map(spec => {
                            return {url: spec.url, xpaths: spec.xpaths} as ClickDriverConfigElement;
                        }));
                        break;
                    }
                    default:
                        break;
                }
            }


        }

        this.projectId = this.data.projectId;

        this.vehicleService.getVehicleOffersForProject$(this.projectId).subscribe(offers => {
            const browsers = offers.map(o => browserFromBackendObject(o!.properties['vehicle.browser']));
            this.availableBrowsers = browsers
                .filter(b => !!b && b!.isActualBrowser())
                .filter(b => !b!.isMobile())
                .sort((a, b) => a!.browserType.localeCompare(b!.browserType));
            this.availableReferenceBrowsers = this.availableBrowsers.filter(b => b!.browserType.toLowerCase() === 'chrome' || b!.browserType.toLowerCase() === 'firefox');
            this.busy = false;
        });

        this.deviceService.getDevicesForProject$(this.projectId).pipe(
            map(devices => {
                return devices.filter(d => d!.isAvailableToJobs() && d!.state === 'running' && !d!.isHeadlessDevice());
            })
        ).subscribe(devices => {
            devices.forEach(d => {
                const machineBrowsers = (d!.properties[DevicePropertyNames.MachineBrowsers] as Array<any>) || [];
                const deployed = machineBrowsers.map(a => browserFromBackendObject(a))
                    .filter(b => b!.isActualBrowser())
                    .filter(b => !b!.isMobile());
                this.deployedBrowsers = [...this.deployedBrowsers, ...deployed];
            });
        });
    }

    private mergeClickDriverConfig(clickDrivers: ClickDriverConfigElement[]) {
        clickDrivers.forEach(cd => {
            let existing = this.clickDriverConfig.find(c => cd.url === c.url);
            if (existing) {
                existing.xpaths = _.uniq([...existing.xpaths, ...cd.xpaths]);
            } else {
                this.clickDriverConfig.push(cd);
            }
        });
    }

    addUrlListItem() {
        this.urls.push('');
        this.crossUrls.push('');
    }

    removeUrlListItem(index: number) {
        this.urls.splice(index, 1);
        this.crossUrls.splice(index, 1);
    }

    trackByIndex(index: number, obj: any): any {
        return index;
    }

    trimURLsHelper(s: string) {
        return s.trim().length;
    }

    setReferenceBrowser(browser: Browser) {
        if (this.data.editMode && this.chosenReferenceBrowser && browser.equalsOtherBrowser(this.chosenReferenceBrowser)) {
            this.chosenReferenceBrowser = undefined;
            this.clearBrowser.emit();
        } else {
            this.chosenReferenceBrowser = browser;
        }
        this.updateBasicAuthIssues();
    }

    updateBasicAuthIssues() {
        let tmpErrorMsg = 'Some Browser(s) you selected do not support Basic Authentication. The test execution will likely fail for: \n\n';
        if (this.chosenReferenceBrowser) {
            let refTmp = !(this.chosenReferenceBrowser.capabilities?.['service.browserMobSupport'] ?? false);
            if (refTmp) {
                tmpErrorMsg += ' - ' + this.chosenReferenceBrowser.getFullName() + '\n\n';
            }
            this.browserWithoutBasicAuthSelected = refTmp;
        }
        if (this.browserWithoutBasicAuthSelected) {
            this.basicAuthErrorMessage = tmpErrorMsg;
        } else {
            this.basicAuthErrorMessage = undefined;
        }
    }

    createTest() {
        this.isCreatingTest = true;
        let conf = {} as ExpeditionComparisonTestConfig;
        let refDriverSpec: DriverSpecification;
        let compDriverSpec: DriverSpecification;
        let baData: BasicAuthInfo[] = [];
        if (this.basicAuthData.length > 0) {
            baData = _.flatten(this.basicAuthData.filter(entry => entry.username.trim().length > 0 && entry.password.trim().length > 0).map(entry => {
                return entry.urls.map(u => {
                    return {
                        basic_auth_domain: u,
                        basic_auth_username: entry.username,
                        basic_auth_password: entry.password
                    };
                });
            }));
        }

        const filteredUrls: string[] = [];
        const filteredCrossUrls: string[] = [];
        this.urls.forEach((u, i) => {
            if (u.length > 0 || this.crossUrls[i].length > 0) {
                filteredUrls.push(u);
                filteredCrossUrls.push(this.crossUrls[i]);
            }
        });

        if (filteredUrls.length > 0) {
            refDriverSpec = {
                urls: filteredUrls,
                type: 'URLListDriverSpecification' as DriverSpecificationType
            };
            compDriverSpec = {
                urls: filteredCrossUrls,
                type: 'URLListDriverSpecification' as DriverSpecificationType
            };

            if (this.clickDriverConfig.length >= 1) {
                refDriverSpec = {
                    type: 'SequentialDriverSpecification' as DriverSpecificationType,
                    specs: [
                        {
                            type: 'ClickDriverSpecification' as DriverSpecificationType,
                            specs: this.clickDriverConfig
                        },
                        refDriverSpec
                    ]
                };
                compDriverSpec = {
                    type: 'SequentialDriverSpecification' as DriverSpecificationType,
                    specs: [
                        {
                            type: 'ClickDriverSpecification' as DriverSpecificationType,
                            specs: this.clickDriverConfig
                        },
                        compDriverSpec
                    ]
                };
            }
        }


        let vehicle: BrowserSpecification;
        if (this.chosenReferenceBrowser) {
            vehicle = {
                // remove browser properties like selenium version to avoid a wrong version being used when running the test
                browser: this.chosenReferenceBrowser.withoutProperties(),
                useProxy: baData.length > 0,
                type: 'BrowserSpecification'
            } as BrowserSpecification;
        }

        if (refDriverSpec! || vehicle!) {
            conf.referenceExpeditionSpec = {
                data: {
                    type: 'LiveExpeditionSpec',
                    driverSpec: refDriverSpec!,
                    vehicleSpec: vehicle!,
                    basicAuth: baData,
                    automationPolicy: this.referenceAutomationPolicy,

                },
                type: 'ExpeditionSpec'
            };
        }

        if (compDriverSpec! || vehicle!) {
            conf.comparisonExpeditionSpecs = {
                data: [{
                    type: 'LiveExpeditionSpec',
                    driverSpec: compDriverSpec!,
                    vehicleSpec: vehicle!,
                    basicAuth: baData
                }],
                type: 'List[ExpeditionSpec]'
            };
        }

        conf.testVisualisationType = {type: 'String', data: 'url_based_regression_test'};

        if (this.data.editMode) {
            conf.name = this.name;
            conf.description = this.description;
        }
        this.data.createCallback(conf);
        if (this.data.closeAfterCallback) {
            this.toolService.closeLastTool();
        }
    }

    checkURLValidity(): boolean {
        if (this.urls.map(this.trimURLsHelper).indexOf(0) === -1 || this.crossUrls.map(this.trimURLsHelper).indexOf(0) === -1) {
            for (let url of this.urls) {
                if (url.length === 0 || !this._urlValid(url)) return false;
            }
            for (let url of this.crossUrls) {
                if (url.length === 0 || !this._urlValid(url)) return false;
            }
            return true;
        } else {
            return false;
        }
    }

    checkURLValidityAllowEmpty() {
        for (let url of this.urls) {
            if (url.length === 0) continue;
            if (!this._urlValid(url)) return false;
        }
        for (let url of this.crossUrls) {
            if (url.length === 0) continue;
            if (!this._urlValid(url)) return false;
        }
        return true;
    }

    private _urlValid(url: string) {
        if (url.indexOf('.') === -1) return false;
        if (url.indexOf('://') === -1) return false;
        if (!url.startsWith('http://') && !url.startsWith('https://')) return false;
        if (url.indexOf('://') !== url.lastIndexOf('://')) return false;
        return true;
    }

    existingURLValid(idx: number): boolean {
        if (idx >= this.urls.length) return false;

        let url = this.urls[idx];
        if (!url || (url.length === 0 && !this.data.editMode)) return false;

        return this._urlValid(url);
    }

    existingCrossURLValid(idx: number): boolean {
        if (idx >= this.crossUrls.length) return false;

        let url = this.crossUrls[idx];
        if (!url || (url.length === 0 && !this.data.editMode)) return false;

        return this._urlValid(url);
    }

    toggleRef() {
        this.simplifiedRef = !this.simplifiedRef;
    }

    toggleAdvancedSettings() {
        this.advancedSettingsShown = !this.advancedSettingsShown;
    }

    mayAddNewUrlPair() {
        if (this.data.editMode) {
            return this.existingURLValid(this.urls.length - 1) || this.existingCrossURLValid(this.urls.length - 1);
        } else {
            return this.existingURLValid(this.urls.length - 1) && this.existingCrossURLValid(this.urls.length - 1);
        }
    }

    settingsValid() {
        if (this.data.editMode) {
            return !this.nameInvalid() &&
                this.checkURLValidityAllowEmpty();
        } else {
            return isDefined(this.chosenReferenceBrowser) &&
                this.urls.length > 0 &&
                this.checkURLValidity();
        }
    }

    clickdriversValid() {
        if (this.clickDriverConfig.length == 0) {
            return true;
        }
        return this.clickDriverConfig.every(clickDriver => this.clickdriverValid(clickDriver));
    }

    clickdriversValidAllowEmpty() {
        if (this.clickDriverConfig.length == 0) {
            return true;
        }
        for (let el of this.clickDriverConfig) {
            if (el.url.length > 0 && !this._urlValid(el.url)) return false;
        }
        return true;
    }

    private _xPathsValid(xpaths: string[]) {
        return (xpaths.length == 0) || (xpaths.every(xpath => xpath.length != 0));
    }

    clickdriverValid(clickDriver: ClickDriverConfigElement) {
        return clickDriver.url.length !== 0 && this._urlValid(clickDriver.url) && this._xPathsValid(clickDriver.xpaths);
    }

    addUrlToClickdriver() {
        this.clickDriverConfig.push({
            url: '',
            xpaths: ['']
        } as ClickDriverConfigElement);
    }

    addXpathToClickdriverURL(clickdriverId: number) {
        this.clickDriverConfig[clickdriverId].xpaths.push('');
    }

    removeClickDriverConfig(clickdriverId: number) {
        this.clickDriverConfig.splice(clickdriverId, 1);
    }

    removeXpathFromClickDriverConfig(clickdriverId: number, xpathid: number) {
        this.clickDriverConfig[clickdriverId].xpaths.splice(xpathid, 1);
    }

    nameInvalid() {
        return this.name.trim().length === 0 || this.data.existingNames.find(n => n === this.name.trim());
    }

    getAllUrlDomains() {
        return _.uniq([...this.urls, ...this.crossUrls].filter(u => this._urlValid(u)).map(url => {
            let u = new URL(url);
            return u.host;
        }));
    }

    getUnusedUrlDomains() {
        return this.getAllUrlDomains().filter(d => !this.basicAuthData.find(ba => ba.urls.find(u => u === d)));
    }

    updateBaUrlOptions() {
        this.baUrlOptions = this.getUnusedUrlDomains().map(d => {
            return {
                value: d,
                label: d
            };
        });
        let allUrlDomains = this.getAllUrlDomains();
        this.basicAuthData.forEach((ba, idx) => {
            ba.urls = ba.urls.filter(url => allUrlDomains.includes(url));
        });
    }

    selectBaUrl(event: SelectChangeEvent, idx: number) {
        this.basicAuthData[idx].urls.push(event.value);
        this.updateBaUrlOptions();
    }

    removeBaUrl(baIdx: number, urlIdx: number) {
        this.basicAuthData[baIdx].urls.splice(urlIdx, 1);
        this.updateBaUrlOptions();
    }

    addBAConfig() {
        this.basicAuthData.push({
            urls: [],
            username: '',
            password: '',
        });
    }

    removeBAConfig(idx: number) {
        this.basicAuthData.splice(idx, 1);
        this.updateBaUrlOptions();
    }

}
